import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
    Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import * as api from '../../api/api';
import RatesTab from './RatesTab';
import ApplicationsTab from './ApplicationsTab';
import DepartmentsTab from './DepartmentsTab';
import TagsTab from './TagsTab';
import AddMemberModal from '../shared/AddMemberModal';
import { calculateMemberSalary, validateMemberRoleMapping, getStandardizedRole } from '../../utils/rateUtils';
import FuzzyMatchWarning from '../FuzzyMatchWarning';
import { buttonStyles, formControlStyles, layoutStyles } from '../../styles/commonStyles';
import { ActionButton, BackButton, StyledFormControl, PageHeader } from '../shared/StyledComponents';

function AdminPage({ onBackToHome, isReadOnly = false }) {
  const [activeTab, setActiveTab] = useState(0);
  
          // IT Roster state
  const [itRoster, setItRoster] = useState([]);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [rateAvailabilityFilter, setRateAvailabilityFilter] = useState('All');
  const [memberRateStatus, setMemberRateStatus] = useState({});
  const [rateCalculationProgress, setRateCalculationProgress] = useState({ current: 0, total: 0, isCalculating: false });
  
  // Tags state
  const [tags, setTags] = useState([]);
  
  

  // Owners state
  const [owners, setOwners] = useState({
    business: [],
    functional: [],
    data: [],
    it: []
  });
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [ownerForm, setOwnerForm] = useState({
    name: '',
    type: 'business'
  });

  // COLA Settings state
  const [colaSettings, setCOLASettings] = useState({ annual_cola_percentage: 4.5 });
  
  // Common state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Listen for edit mode changes
  useEffect(() => {
    const handleEditModeChange = (event) => {
      // Force re-render when edit mode changes
      setError('');
    };

    window.addEventListener('editModeChanged', handleEditModeChange);
    
    return () => {
      window.removeEventListener('editModeChanged', handleEditModeChange);
    };
  }, []);

        useEffect(() => {
    fetchITRoster();
    fetchOwners();
    fetchCOLASettings();
    fetchTags();
  }, []);

        // Calculate rate availability for all members with debouncing and batching
  useEffect(() => {
    let timeoutId;
    
    const calculateRateAvailability = async () => {
      const statusMap = {};
      const currentYear = new Date().getFullYear();
      
      if (itRoster.length === 0) {
        setMemberRateStatus({});
        return;
      }
      
            console.log(`🔄 AdminPage: Starting rate calculation for ${itRoster.length} members`);
      setRateCalculationProgress({ current: 0, total: itRoster.length, isCalculating: true });
      
            // Set quiet mode for rate calculations to reduce console noise
      window.rateCalculationQuietMode = true;
      
      try {
        // Process members in smaller batches to avoid overwhelming the system
        const BATCH_SIZE = 5;
        const batches = [];
        for (let i = 0; i < itRoster.length; i += BATCH_SIZE) {
          batches.push(itRoster.slice(i, i + BATCH_SIZE));
        }
        
        let processedCount = 0;
        
        for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
          const batch = batches[batchIndex];
          if (batchIndex === 0 || batchIndex === batches.length - 1) {
            console.log(`🔄 AdminPage: Processing batch ${batchIndex + 1}/${batches.length} (${batch.length} members)`);
          }
        
        // Process batch members in parallel but limit concurrency
        const batchPromises = batch.map(async (member) => {
          try {
            // Use cached results if available and recent (within 5 minutes)
            const cacheKey = `member_rate_${member.id}_${currentYear}`;
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
              const cachedData = JSON.parse(cached);
              const cacheAge = Date.now() - cachedData.timestamp;
              if (cacheAge < 5 * 60 * 1000) { // 5 minutes
                return { memberId: member.id, status: cachedData.status };
              }
            }
            
                                    // Calculate fresh rate data with reduced logging for batch processing
            const [validation, salaryInfo] = await Promise.all([
              validateMemberRoleMapping(member),
              calculateMemberSalary(member, currentYear)
            ]);
            
            const status = {
              available: salaryInfo.monthlySalary > 0 && !salaryInfo.error,
              monthlySalary: salaryInfo.monthlySalary,
              annualSalary: salaryInfo.annualSalary,
              error: salaryInfo.error,
              isAdjusted: salaryInfo.isAdjusted,
              standardizedRole: salaryInfo.standardizedRole,
              isFuzzyMatch: salaryInfo.isFuzzyMatch,
              fuzzyMatchInfo: salaryInfo.fuzzyMatchInfo,
              validation: {
                isValid: validation.isValid,
                memberKey: validation.memberKey,
                standardizedKey: validation.standardizedKey,
                rateKey: validation.rateKey,
                matchedRate: validation.matchedRate,
                allMatchingRates: validation.allMatchingRates,
                issue: validation.issue,
                suggestions: validation.suggestions,
                availableRoles: validation.availableRoles
              },
              mappingDetails: {
                originalRole: member.role,
                standardizedRole: getStandardizedRole(member.role),
                company: member.company,
                location: member.location,
                searchKey: `${member.company}/${member.location}/${getStandardizedRole(member.role)}`
              }
            };
            
            // Cache the result
            sessionStorage.setItem(cacheKey, JSON.stringify({
              status,
              timestamp: Date.now()
            }));
            
            return { memberId: member.id, status };
            
          } catch (error) {
            const errorStatus = {
              available: false,
              monthlySalary: 0,
              annualSalary: 0,
              error: error.message || 'Rate calculation failed',
              isAdjusted: false,
              standardizedRole: null,
              isFuzzyMatch: false,
              fuzzyMatchInfo: null,
              validation: {
                isValid: false,
                issue: error.message || 'Validation failed',
                suggestions: ['Check member data completeness', 'Verify rate data availability']
              },
              mappingDetails: {
                originalRole: member.role || 'Missing',
                standardizedRole: member.role ? getStandardizedRole(member.role) : 'Missing',
                company: member.company || 'Missing',
                location: member.location || 'Missing',
                searchKey: 'Invalid'
              }
            };
            
            return { memberId: member.id, status: errorStatus };
          }
        });
        
        // Wait for batch to complete
        const batchResults = await Promise.all(batchPromises);
        
        // Update status map with batch results
        batchResults.forEach(({ memberId, status }) => {
          statusMap[memberId] = status;
        });
        
                // Update progress
        processedCount += batch.length;
        setRateCalculationProgress({ current: processedCount, total: itRoster.length, isCalculating: true });
        
        // Update UI with partial results to show progress
        setMemberRateStatus(prevStatus => ({ ...prevStatus, ...statusMap }));
        
        // Small delay between batches to prevent overwhelming the system
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
              setRateCalculationProgress({ current: itRoster.length, total: itRoster.length, isCalculating: false });
        console.log(`✅ AdminPage: Completed rate calculation for ${itRoster.length} members`);
        
      } finally {
        // Always restore normal logging mode
        window.rateCalculationQuietMode = false;
      }
    };
    
    // Debounce the calculation to prevent excessive calls
    if (itRoster.length > 0) {
      timeoutId = setTimeout(calculateRateAvailability, 500);
        } else {
      setMemberRateStatus({});
      setRateCalculationProgress({ current: 0, total: 0, isCalculating: false });
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [itRoster]);

    // Tags functions
  const fetchTags = async () => {
    try {
      const response = await api.getTags();
      setTags(response.tags || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]);
    }
  };

  // Helper function to get tag name from ID
  const getTagName = (tagId) => {
    const tag = tags.find(t => t.id === tagId);
    return tag ? tag.name : `Unknown Tag (${tagId})`;
  };

  // Helper function to get tag names for a member
  const getMemberTagNames = (member) => {
    if (!member.tags || !Array.isArray(member.tags) || member.tags.length === 0) {
      return [];
    }
    return member.tags.map(tagId => getTagName(tagId));
  };

  // IT Roster functions
    const fetchITRoster = async () => {
        try {
      const response = await api.getITRoster();
      setItRoster(response);
    } catch (error) {
      console.error('Error fetching IT roster:', error);
      setError('Failed to load IT roster');
    }
  };

    const handleAddMember = () => {
    if (isReadOnly) {
      setError('Cannot add members in read-only mode');
      return;
    }

    setEditingMember(null);
    setAddMemberModalOpen(true);
  };

    const handleEditMember = (member) => {
    if (isReadOnly) {
      setError('Cannot edit members in read-only mode');
      return;
    }

    setEditingMember(member);
    setAddMemberModalOpen(true);
  };

                const handleMemberAdded = async (memberData) => {
    // Refresh the IT roster after adding/updating a member
    await fetchITRoster();
    
    // Clear rate cache for the affected member
    if (memberData?.id) {
      const currentYear = new Date().getFullYear();
      const cacheKey = `member_rate_${memberData.id}_${currentYear}`;
      sessionStorage.removeItem(cacheKey);
    }
    
    // If this was an edit (not add), check if rate-affecting fields changed
    if (editingMember && memberData?.id) {
      const rateAffectingFieldsChanged = (
        editingMember.company !== memberData.company ||
        editingMember.location !== memberData.location ||
        editingMember.role !== memberData.role
      );
      
      if (rateAffectingFieldsChanged) {
        console.log(`Rate-affecting fields changed for ${memberData.name}, propagating to outcomes...`);
        
        // Update member information across all outcomes in the background
        propagateMemberChangesToOutcomes(memberData, editingMember)
          .then(result => {
            if (result.success) {
              console.log(`✅ Successfully updated member ${memberData.name} in ${result.updatedOutcomes} outcomes`);
              
              // Update the member's status to show propagation occurred
              setMemberRateStatus(prevStatus => ({
                ...prevStatus,
                [memberData.id]: {
                  ...prevStatus[memberData.id],
                  lastPropagated: new Date().toISOString(),
                  propagatedToOutcomes: result.updatedOutcomes
                }
              }));
            } else {
              console.warn(`⚠️ Member update propagation completed with issues:`, result);
            }
          })
          .catch(error => {
            console.error(`❌ Failed to propagate member changes:`, error);
          });
      }
    }
    
    setSuccess(`IT roster member ${editingMember ? 'updated' : 'added'} successfully`);
    setTimeout(() => setSuccess(''), 3000);
    
    // Close the modal and reset state
    setAddMemberModalOpen(false);
    setEditingMember(null);
  };

  const handleCloseAddMemberModal = () => {
    setAddMemberModalOpen(false);
    setEditingMember(null);
  };

    const handleDeleteMember = async (memberId) => {
    if (isReadOnly) {
      setError('Cannot delete members in read-only mode');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this member?')) return;
    
    try {
      setLoading(true);
      
      // Clear rate cache for the deleted member
      const currentYear = new Date().getFullYear();
      const cacheKey = `member_rate_${memberId}_${currentYear}`;
      sessionStorage.removeItem(cacheKey);
      
      await api.deleteITRosterMember(memberId);
      await fetchITRoster();
      setSuccess('Member deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting member:', error);
      setError('Failed to delete member');
    } finally {
      setLoading(false);
    }
  };

  // Owners functions
  const fetchOwners = async () => {
    try {
      const response = await api.getOwners();
      setOwners(response);
    } catch (error) {
      console.error('Error fetching owners:', error);
      setError('Failed to load owners');
    }
  };

  const handleAddOwner = () => {
    if (isReadOnly) {
      setError('Cannot add owners in read-only mode');
      return;
    }

    setEditingOwner(null);
    setOwnerForm({
      name: '',
      type: 'business'
    });
    setOwnerDialogOpen(true);
  };

  const handleEditOwner = (owner) => {
    if (isReadOnly) {
      setError('Cannot edit owners in read-only mode');
      return;
    }

    setEditingOwner(owner);
    setOwnerForm({
      name: owner.name || '',
      type: owner.type || 'business'
    });
    setOwnerDialogOpen(true);
  };

  const handleSaveOwner = async () => {
    if (isReadOnly) {
      setError('Cannot save changes in read-only mode');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
            if (editingOwner) {
        await api.updateOwner(editingOwner.id, {
          name: ownerForm.name,
          type: ownerForm.type
        });
      } else {
        await api.addOwner({
          name: ownerForm.name,
          type: ownerForm.type
        });
      }
      
      await fetchOwners();
      setOwnerDialogOpen(false);
      setSuccess('Owner saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving owner:', error);
      setError('Failed to save owner');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOwner = async (ownerId) => {
    if (isReadOnly) {
      setError('Cannot delete owners in read-only mode');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this owner?')) return;
    
    try {
      setLoading(true);
      await api.deleteOwner(ownerId);
      await fetchOwners();
      setSuccess('Owner deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting owner:', error);
      setError('Failed to delete owner');
    } finally {
      setLoading(false);
    }
  };

  // COLA Settings functions
  const fetchCOLASettings = async () => {
    try {
      const response = await api.getCOLASettings();
      setCOLASettings(response);
    } catch (error) {
      console.error('Error fetching COLA settings:', error);
      setCOLASettings({ annual_cola_percentage: 4.5 });
    }
  };

  const handleSaveCOLA = async () => {
    if (isReadOnly) {
      setError('Cannot save settings in read-only mode');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await api.updateCOLASettings(colaSettings);
      setSuccess('COLA settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving COLA settings:', error);
      setError('Failed to save COLA settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

      // Memoize helper functions to prevent unnecessary re-renders
  const formatMemberFunction = useCallback((member) => {
    if (member.function === 'Developer' && member.focus) {
      return `${member.function} (${member.focus})`;
    }
    return member.function;
  }, []);
  
  // Memoize filtered roster to prevent recalculation on every render
  const filteredRoster = useMemo(() => {
    if (rateAvailabilityFilter === 'All') return itRoster;
    
    return itRoster.filter((member) => {
      const rateStatus = memberRateStatus[member.id];
      if (!rateStatus) return rateAvailabilityFilter === 'Missing';
      return rateAvailabilityFilter === 'Available' ? rateStatus.available : !rateStatus.available;
    });
  }, [itRoster, memberRateStatus, rateAvailabilityFilter]);

    // Helper function to get rate calculation summary
  const getRateCalculationSummary = () => {
    const statusValues = Object.values(memberRateStatus);
    return {
      total: itRoster.length,
      calculated: statusValues.length,
      available: statusValues.filter(status => status?.available).length,
      missing: statusValues.filter(status => status && !status.available).length,
      fuzzyMatches: statusValues.filter(status => status?.isFuzzyMatch).length,
      colaAdjusted: statusValues.filter(status => status?.isAdjusted).length,
      calculating: rateCalculationProgress.isCalculating,
      progress: rateCalculationProgress
    };
  };

    // Propagate member changes to all outcomes where the member is staffed
  const propagateMemberChangesToOutcomes = async (updatedMember, originalMember) => {
    try {
      console.log(`🔄 Starting propagation of member changes for ${updatedMember.name}`);
      
      // Find all outcomes where this member is staffed
      const outcomesResponse = await api.findOutcomesWithMember(updatedMember.id);
      const affectedOutcomes = outcomesResponse.outcomes || [];
      
      if (affectedOutcomes.length === 0) {
        console.log(`No outcomes found with member ${updatedMember.name}`);
        return { success: true, updatedOutcomes: 0 };
      }
      
      console.log(`Found ${affectedOutcomes.length} outcomes with member ${updatedMember.name}`);
      
      // Prepare the member update data (only rate-affecting fields)
      const memberUpdateData = {
        id: updatedMember.id,
        name: updatedMember.name,
        company: updatedMember.company,
        location: updatedMember.location,
        role: updatedMember.role,
        function: updatedMember.function,
        focus: updatedMember.focus,
        department: updatedMember.department
      };
      
      // Batch update outcomes (process in groups of 5 to avoid overwhelming the backend)
      const BATCH_SIZE = 5;
      const batches = [];
      for (let i = 0; i < affectedOutcomes.length; i += BATCH_SIZE) {
        batches.push(affectedOutcomes.slice(i, i + BATCH_SIZE));
      }
      
      let totalUpdated = 0;
      let totalFailed = 0;
      
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        console.log(`Processing batch ${batchIndex + 1}/${batches.length}: ${batch.length} outcomes`);
        
        // Process batch in parallel
        const batchPromises = batch.map(async (outcome) => {
          try {
            await api.updateMemberInOutcome(outcome.id, memberUpdateData);
            return { outcomeId: outcome.id, success: true };
          } catch (error) {
            console.error(`Failed to update member in outcome ${outcome.id}:`, error);
            return { outcomeId: outcome.id, success: false, error: error.message };
          }
        });
        
        const batchResults = await Promise.allSettled(batchPromises);
        
        // Count successes and failures
        batchResults.forEach(result => {
          if (result.status === 'fulfilled' && result.value.success) {
            totalUpdated++;
          } else {
            totalFailed++;
          }
        });
        
        // Small delay between batches
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
      
      console.log(`✅ Propagation complete: ${totalUpdated} updated, ${totalFailed} failed`);
      
      return {
        success: totalFailed === 0,
        updatedOutcomes: totalUpdated,
        failedOutcomes: totalFailed,
        totalOutcomes: affectedOutcomes.length
      };
      
    } catch (error) {
      console.error('Error propagating member changes:', error);
      return {
        success: false,
        error: error.message,
        updatedOutcomes: 0
      };
    }
  };

  // Debug function to test rate matching for a specific member
  const testMemberRateMatching = async (memberId) => {
    const member = itRoster.find(m => m.id === memberId);
    if (!member) {
      console.error('Member not found:', memberId);
      return;
    }

    console.group(`🔍 Testing Rate Matching for ${member.name}`);
    
    try {
      const currentYear = new Date().getFullYear();
      
      console.log('👤 Member Details:', {
        id: member.id,
        name: member.name,
        company: member.company,
        location: member.location,
        function: member.function,
        role: member.role,
        focus: member.focus
      });
      
      // Test validation
      const validation = await validateMemberRoleMapping(member);
      console.log('🔍 Validation Result:', validation);
      
      // Test salary calculation
      const salaryInfo = await calculateMemberSalary(member, currentYear);
      console.log('💰 Salary Calculation:', salaryInfo);
      
      // Show current status from state
      const currentStatus = memberRateStatus[member.id];
      console.log('📊 Current Status in UI:', currentStatus);
      
    } catch (error) {
      console.error('❌ Test failed:', error);
    } finally {
      console.groupEnd();
    }
  };

      // Make test function available globally for debugging
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.testMemberRateMatching = testMemberRateMatching;
      window.refreshMemberRateStatus = () => {
        // Clear cache and force recalculation
        sessionStorage.removeItem('member_rate_cache');
        const currentYear = new Date().getFullYear();
        itRoster.forEach(member => {
          const cacheKey = `member_rate_${member.id}_${currentYear}`;
          sessionStorage.removeItem(cacheKey);
        });
        setMemberRateStatus({});
      };
      window.testFuzzyMatchWarnings = () => {
        console.group('🔍 Testing Fuzzy Match Warnings');
        const membersWithFuzzyMatches = itRoster.filter(member => {
          const rateStatus = memberRateStatus[member.id];
          return rateStatus?.isFuzzyMatch && rateStatus?.fuzzyMatchInfo;
        });
        
        console.log(`Found ${membersWithFuzzyMatches.length} members with fuzzy matches:`);
        membersWithFuzzyMatches.forEach(member => {
          const rateStatus = memberRateStatus[member.id];
          console.log(`- ${member.name}: "${rateStatus.fuzzyMatchInfo.originalRole}" → "${rateStatus.fuzzyMatchInfo.matchedRole}"`);
        });
        
        if (membersWithFuzzyMatches.length === 0) {
          console.log('No fuzzy matches found. All members have exact rate matches or no rates available.');
        }
        
        console.groupEnd();
        return membersWithFuzzyMatches;
      };
            window.clearRateCache = () => {
        // Clear all rate calculation caches
        const keys = Object.keys(sessionStorage).filter(key => key.startsWith('member_rate_'));
        keys.forEach(key => sessionStorage.removeItem(key));
        console.log(`Cleared ${keys.length} cached rate calculations`);
      };
      window.getRateCalculationSummary = getRateCalculationSummary;
      window.logRateCalculationSummary = () => {
        const summary = getRateCalculationSummary();
        console.group('📊 Rate Calculation Summary');
        console.log('Total Members:', summary.total);
        console.log('Rates Calculated:', summary.calculated);
        console.log('Rates Available:', summary.available);
        console.log('Rates Missing:', summary.missing);
        console.log('Fuzzy Matches:', summary.fuzzyMatches);
        console.log('COLA Adjusted:', summary.colaAdjusted);
        console.log('Currently Calculating:', summary.calculating);
        if (summary.calculating) {
          console.log('Progress:', `${summary.progress.current}/${summary.progress.total}`);
        }
        console.groupEnd();
        return summary;
      };
    }
  }, [itRoster, memberRateStatus]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {onBackToHome && (
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBackToHome}
          >
            Home
          </Button>
        )}
        <Box>
          <Typography variant="h4">Administration</Typography>
          {isReadOnly && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Read-Only Mode - Changes are not permitted
            </Typography>
          )}
        </Box>
      </Box>

                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="IT Roster" />
          <Tab label="Owners" />
          <Tab label="Applications" />
          <Tab label="Departments" />
          <Tab label="Tags" />
          <Tab label="COLA Settings" />
          <Tab label="Rates" />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* IT Roster Tab */}
      {activeTab === 0 && (
        <Card elevation={2}>
                    <CardHeader
            title="IT Roster Management"
            subheader="Manage IT team members and their rates"
                        action={
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <StyledFormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Rate Filter</InputLabel>
                  <Select
                    value={rateAvailabilityFilter}
                    label="Rate Filter"
                    onChange={(e) => setRateAvailabilityFilter(e.target.value)}
                  >
                                        <MenuItem value="All">
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span>All Members</span>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          ml: 1, 
                          minWidth: 'auto',
                          bgcolor: 'grey.100',
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.5,
                          fontSize: '0.7rem'
                        }}>
                          {itRoster.length}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="Available">
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span>Rate Available</span>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          ml: 1, 
                          minWidth: 'auto',
                          bgcolor: 'success.50',
                          color: 'success.dark',
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.5,
                          fontSize: '0.7rem'
                        }}>
                          {Object.values(memberRateStatus).filter(status => status?.available).length}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="Missing">
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span>Rate Missing</span>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          ml: 1, 
                          minWidth: 'auto',
                          bgcolor: 'error.50',
                          color: 'error.dark',
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.5,
                          fontSize: '0.7rem'
                        }}>
                          {Object.values(memberRateStatus).filter(status => status && !status.available).length}
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </StyledFormControl>
                                <ActionButton
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    // Clear all rate caches and force recalculation
                    const currentYear = new Date().getFullYear();
                    itRoster.forEach(member => {
                      const cacheKey = `member_rate_${member.id}_${currentYear}`;
                      sessionStorage.removeItem(cacheKey);
                    });
                    setMemberRateStatus({});
                    setRateCalculationProgress({ current: 0, total: 0, isCalculating: false });
                  }}
                  disabled={rateCalculationProgress.isCalculating}
                  size="small"
                >
                  Refresh Rates
                </ActionButton>
                <ActionButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddMember}
                  disabled={isReadOnly}
                >
                  Add Member
                </ActionButton>
              </Box>
            }
          />
          <CardContent>
                        {/* Rate Calculation Progress */}
            {rateCalculationProgress.isCalculating && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Calculating rates for IT roster members...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {rateCalculationProgress.current} / {rateCalculationProgress.total}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(rateCalculationProgress.current / rateCalculationProgress.total) * 100}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            )}
            
                                    {/* Rate Summary - Subtle Stats */}
            {!rateCalculationProgress.isCalculating && Object.keys(memberRateStatus).length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                mb: 2, 
                p: 1.5, 
                bgcolor: 'grey.25', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.100'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant="caption" sx={{ 
                    bgcolor: 'success.50',
                    color: 'success.dark',
                    px: 1,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    minWidth: '20px',
                    textAlign: 'center'
                  }}>
                    {Object.values(memberRateStatus).filter(status => status?.available).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Available
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant="caption" sx={{ 
                    bgcolor: 'error.50',
                    color: 'error.dark',
                    px: 1,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    minWidth: '20px',
                    textAlign: 'center'
                  }}>
                    {Object.values(memberRateStatus).filter(status => status && !status.available).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Missing
                  </Typography>
                </Box>
                {Object.values(memberRateStatus).filter(status => status?.isFuzzyMatch).length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="caption" sx={{ 
                      bgcolor: 'warning.50',
                      color: 'warning.dark',
                      px: 1,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      minWidth: '20px',
                      textAlign: 'center'
                    }}>
                      {Object.values(memberRateStatus).filter(status => status?.isFuzzyMatch).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Fuzzy Matches
                    </Typography>
                  </Box>
                )}
                {Object.values(memberRateStatus).filter(status => status?.isAdjusted).length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="caption" sx={{ 
                      bgcolor: 'info.50',
                      color: 'info.dark',
                      px: 1,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      minWidth: '20px',
                      textAlign: 'center'
                    }}>
                      {Object.values(memberRateStatus).filter(status => status?.isAdjusted).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      COLA Adjusted
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                    <TableCell>Function</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell>Rate Available</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                                                                                <TableBody>
                                    {filteredRoster.map((member) => (
                                      <TableRow key={member.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {member.name}
                            </Typography>
                            {(() => {
                              const rateStatus = memberRateStatus[member.id];
                              if (rateStatus?.lastPropagated) {
                                const propagatedDate = new Date(rateStatus.lastPropagated);
                                const isRecent = Date.now() - propagatedDate.getTime() < 5 * 60 * 1000; // 5 minutes
                                return (
                                  <Tooltip title={`Member data propagated to ${rateStatus.propagatedToOutcomes || 0} outcomes at ${propagatedDate.toLocaleTimeString()}`}>
                                    <Box sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      bgcolor: isRecent ? 'success.50' : 'grey.100',
                                      color: isRecent ? 'success.dark' : 'text.secondary',
                                      px: 0.5,
                                      py: 0.25,
                                      borderRadius: 0.5,
                                      fontSize: '0.6rem'
                                    }}>
                                      📤
                                    </Box>
                                  </Tooltip>
                                );
                              }
                              return null;
                            })()
                            }
                          </Box>
                          {(() => {
                            const rateStatus = memberRateStatus[member.id];
                            if (rateStatus?.mappingDetails) {
                              return (
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
                                  Key: {rateStatus.mappingDetails.searchKey}
                                </Typography>
                              );
                            }
                            return null;
                          })()
                          }
                        </Box>
                      </TableCell>
                      <TableCell>{formatMemberFunction(member)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            label={String(member.company || '')}
                            size="small"
                            variant="outlined"
                            color="success"
                          />
                          {member.department && (
                            <Chip
                              label={String(member.department)}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={String(member.location || '')}
                          size="small"
                          variant="outlined"
                          color="warning"
                        />
                      </TableCell>
                                            <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">
                              {member.role}
                            </Typography>
                            {(() => {
                              const rateStatus = memberRateStatus[member.id];
                              if (rateStatus?.isFuzzyMatch && rateStatus?.fuzzyMatchInfo) {
                                return (
                                  <FuzzyMatchWarning 
                                    role={rateStatus}
                                    variant="chip"
                                    sx={{ ml: 0.5 }}
                                  />
                                );
                              }
                              return null;
                            })()
                            }
                          </Box>
                                                    {(() => {
                            const rateStatus = memberRateStatus[member.id];
                            if (rateStatus?.mappingDetails?.standardizedRole !== rateStatus?.mappingDetails?.originalRole) {
                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" color="primary" sx={{ fontSize: '0.65rem' }}>
                                    → {rateStatus.mappingDetails.standardizedRole}
                                  </Typography>
                                  {rateStatus?.isFuzzyMatch && rateStatus?.fuzzyMatchInfo && (
                                    <FuzzyMatchWarning 
                                      role={rateStatus}
                                      variant="icon"
                                      sx={{ fontSize: '0.6rem' }}
                                    />
                                  )}
                                </Box>
                              );
                            }
                            return null;
                                                    })()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {getMemberTagNames(member).map((tagName, index) => (
                            <Chip
                              key={index}
                              label={tagName}
                              size="small"
                              variant="outlined"
                              color="info"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                          {(!member.tags || member.tags.length === 0) && (
                            <Typography variant="caption" color="text.secondary">
                              No tags
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                                                                  <TableCell>
                        {(() => {
                          const rateStatus = memberRateStatus[member.id];
                          if (!rateStatus) {
                            return (
                              <Chip
                                label="Loading..."
                                size="small"
                                color="default"
                                variant="outlined"
                              />
                            );
                          }
                          
                          if (rateStatus.available) {
                            return (
                              <Tooltip
                                title={
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                      ✅ Exact Rate Match Found
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                      Member Mapping:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                      {rateStatus.mappingDetails?.searchKey}
                                    </Typography>
                                    
                                    {rateStatus.validation?.rateKey && (
                                      <>
                                        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                                          Matched Rate:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                          {rateStatus.validation.rateKey}
                                        </Typography>
                                      </>
                                    )}
                                    
                                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                      Rate Details:
                                    </Typography>
                                    <Typography variant="body2">
                                      Monthly: ${rateStatus.monthlySalary?.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2">
                                      Annual: ${rateStatus.annualSalary?.toLocaleString()}
                                    </Typography>
                                    
                                    {rateStatus.mappingDetails?.standardizedRole !== rateStatus.mappingDetails?.originalRole && (
                                      <>
                                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                          Role Mapping:
                                        </Typography>
                                        <Typography variant="body2">
                                          '{rateStatus.mappingDetails?.originalRole}' → '{rateStatus.mappingDetails?.standardizedRole}'
                                        </Typography>
                                      </>
                                    )}
                                    
                                                                        {rateStatus.isAdjusted && (
                                      <Typography variant="body2" sx={{ mt: 1, color: 'warning.main', fontWeight: 'bold' }}>
                                        📈 COLA Adjusted Rate
                                      </Typography>
                                    )}
                                    
                                    {rateStatus.isFuzzyMatch && rateStatus.fuzzyMatchInfo && (
                                      <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                                          ⚠️ Fuzzy Rate Mapping
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5, color: 'warning.dark' }}>
                                          Role "{rateStatus.fuzzyMatchInfo.originalRole}" was mapped to rate for "{rateStatus.fuzzyMatchInfo.matchedRole}"
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                                          Consider updating the member's role or adding a specific rate for "{rateStatus.fuzzyMatchInfo.originalRole}"
                                        </Typography>
                                      </Box>
                                    )}
                                    
                                    {rateStatus.validation?.allMatchingRates && rateStatus.validation.allMatchingRates.length > 1 && (
                                      <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem' }}>
                                        {rateStatus.validation.allMatchingRates.length} rate years available
                                      </Typography>
                                    )}
                                  </Box>
                                }
                                arrow
                              >
                                <Chip
                                  label="Yes"
                                  size="small"
                                  color="success"
                                  variant="filled"
                                />
                              </Tooltip>
                            );
                          } else {
                            return (
                              <Tooltip
                                title={
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                      ❌ No Exact Rate Match
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                      Member Search Key:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                      {rateStatus.mappingDetails?.searchKey || 'Invalid'}
                                    </Typography>
                                    
                                    {rateStatus.mappingDetails?.standardizedRole !== rateStatus.mappingDetails?.originalRole && (
                                      <>
                                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                          Role Mapping:
                                        </Typography>
                                        <Typography variant="body2">
                                          '{rateStatus.mappingDetails?.originalRole}' → '{rateStatus.mappingDetails?.standardizedRole}'
                                        </Typography>
                                      </>
                                    )}
                                    
                                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                      Issue:
                                    </Typography>
                                    <Typography variant="body2">
                                      {rateStatus.validation?.issue || rateStatus.error || 'No matching rate found'}
                                    </Typography>
                                    
                                    {rateStatus.validation?.availableRoles && rateStatus.validation.availableRoles.length > 0 && (
                                      <>
                                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                          Available Roles at {member.company}/{member.location}:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                          {rateStatus.validation.availableRoles.join(', ')}
                                        </Typography>
                                      </>
                                    )}
                                    
                                    {rateStatus.validation?.suggestions && rateStatus.validation.suggestions.length > 0 && (
                                      <>
                                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                          Suggestions:
                                        </Typography>
                                        {rateStatus.validation.suggestions.slice(0, 3).map((suggestion, index) => (
                                          <Typography key={index} variant="body2" sx={{ fontSize: '0.75rem' }}>
                                            • {suggestion}
                                          </Typography>
                                        ))}
                                      </>
                                    )}
                                    
                                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                      Required Data:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                      • Company: {member.company || '❌ Missing'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                      • Location: {member.location || '❌ Missing'}
                                    </Typography>
                                                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                      • Role: {member.role || '❌ Missing'}
                                    </Typography>
                                    
                                    {rateStatus.isFuzzyMatch && rateStatus.fuzzyMatchInfo && (
                                      <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                                          ⚠️ Fuzzy Match Attempted
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5, color: 'warning.dark' }}>
                                          Found similar role "{rateStatus.fuzzyMatchInfo.matchedRole}" but rate calculation failed
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                                          Consider updating the member's role to "{rateStatus.fuzzyMatchInfo.matchedRole}" or adding a specific rate
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                }
                                arrow
                              >
                                <Chip
                                  label="No"
                                  size="small"
                                  color="error"
                                  variant="filled"
                                  icon={<InfoIcon sx={{ fontSize: '0.8rem' }} />}
                                />
                              </Tooltip>
                            );
                          }
                        })()
                      }</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEditMember(member)}
                          disabled={isReadOnly}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteMember(member.id)}
                          color="error"
                          disabled={isReadOnly}
                        >
                          <DeleteIcon />
                        </IconButton>
                                            </TableCell>
                                        </TableRow>
                  ))}
                                                                        {itRoster.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No IT roster members found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                                    {filteredRoster.length === 0 && itRoster.length > 0 && !rateCalculationProgress.isCalculating && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No members match the current filter criteria
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {itRoster.length > 0 && rateCalculationProgress.isCalculating && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Processing rate calculations... ({rateCalculationProgress.current}/{rateCalculationProgress.total})
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Owners Tab */}
      {activeTab === 1 && (
        <Card elevation={2}>
          <CardHeader
            title="Owners Management"
            subheader="Manage business, functional, data, and IT owners"
            action={
                            <ActionButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddOwner}
                disabled={isReadOnly}
              >
                Add Owner
              </ActionButton>
            }
          />
          <CardContent>
            {Object.entries(owners).map(([type, ownerList]) => (
              <Box key={type} sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ textTransform: 'capitalize' }}>
                  {type} Owners
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ownerList.map((owner) => (
                        <TableRow key={owner.id}>
                          <TableCell>{owner.name}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleEditOwner(owner)}
                              disabled={isReadOnly}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteOwner(owner.id)}
                              color="error"
                              disabled={isReadOnly}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {ownerList.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2} align="center">
                            No {type} owners found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

                  {/* Applications Tab */}
      {activeTab === 2 && (
        <ApplicationsTab />
      )}

      {/* Departments Tab */}
      {activeTab === 3 && (
        <DepartmentsTab />
      )}

      {/* Tags Tab */}
      {activeTab === 4 && (
        <TagsTab />
      )}

      {/* COLA Settings Tab */}
      {activeTab === 5 && (
        <Card elevation={2}>
          <CardHeader 
            title="Cost of Living Adjustment (COLA) Settings"
            subheader="Configure the annual cost increase percentage used across the application"
          />
          <CardContent>
            <Box sx={{ maxWidth: 400 }}>
              <TextField
                label="Annual COLA Percentage"
                type="number"
                value={colaSettings.annual_cola_percentage}
                onChange={(e) => setCOLASettings({
                  ...colaSettings,
                  annual_cola_percentage: parseFloat(e.target.value) || 0
                })}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{ 
                  min: 0, 
                  max: 20, 
                  step: 0.1 
                }}
                helperText="This percentage will be used for cost projections in financial models and planning calculations"
                sx={{ mb: 3 }}
                disabled={isReadOnly}
              />

                            <ActionButton
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveCOLA}
                disabled={loading || isReadOnly}
              >
                {loading ? 'Saving...' : 'Save COLA Settings'}
              </ActionButton>
            </Box>

            <Box sx={{ mt: 4, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Usage Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • This COLA rate is used in Financial Model projections<br/>
                • Applied to 1-year savings calculations in Planning<br/>
                • Used for cost escalation in multi-year analyses<br/>
                • Typical range: 2-6% annually
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

                  {/* Rates Tab */}
      {activeTab === 6 && (
        <RatesTab />
      )}

                  {/* Add/Edit Member Modal */}
      <AddMemberModal
        open={addMemberModalOpen}
        onClose={handleCloseAddMemberModal}
        onMemberAdded={handleMemberAdded}
        editingMember={editingMember}
        readOnly={isReadOnly}
      />

      {/* Owner Dialog */}
      <Dialog open={ownerDialogOpen} onClose={() => setOwnerDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editingOwner ? 'Edit Owner' : 'Add Owner'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={ownerForm.name}
            onChange={(e) => setOwnerForm({ ...ownerForm, name: e.target.value })}
            fullWidth
            sx={{ mb: 2, mt: 1 }}
            disabled={isReadOnly}
          />
          {!editingOwner && (
                        <StyledFormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={ownerForm.type}
                label="Type"
                onChange={(e) => setOwnerForm({ ...ownerForm, type: e.target.value })}
                disabled={isReadOnly}
              >
                <MenuItem value="business">Business</MenuItem>
                <MenuItem value="functional">Functional</MenuItem>
                <MenuItem value="data">Data</MenuItem>
                <MenuItem value="it">IT</MenuItem>
              </Select>
            </StyledFormControl>
          )}
        </DialogContent>
                <DialogActions sx={{ p: 3, gap: 2 }}>
          <ActionButton onClick={() => setOwnerDialogOpen(false)} variant="outlined">
            Cancel
          </ActionButton>
          <ActionButton 
            onClick={handleSaveOwner} 
            variant="contained" 
            disabled={loading || isReadOnly}
          >
            {loading ? 'Saving...' : 'Save'}
          </ActionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminPage;